// Choix de la forme plurielle d'une clé (Phase 95, plan 95-09).
//
// Ce module existe pour UNE raison : les deux langues servies ne classent pas
// `0` de la même façon. Le français le met au singulier (« 0 séance »),
// l'anglais au pluriel (« 0 sessions »). Une comparaison du compte à un
// écrite à la main, ou un « s » cousu à la fin d'un mot, se trompe donc dès le
// premier écran vide — l'état exact d'un compte qui vient d'être créé. Le
// tableau de pluriels d'ICU, lui, connaît la règle des
// deux langues et de toutes celles qu'on pourrait servir ensuite.
//
// Plafond assumé : seules les catégories `one` et `other` sont produites, ce
// qui suffit au français et à l'anglais. Une langue à catégories `few` / `many`
// (polonais, russe, arabe) demanderait d'élargir le type de retour et
// d'ajouter les suffixes correspondants aux deux catalogues — voie
// d'agrandissement, pas un manque d'aujourd'hui. Toute catégorie non couverte
// retombe sur `_other`, qui reste une phrase lisible.
//
// Pas de mémoïsation : le moteur met déjà ses formateurs en cache, et un cache
// maison serait du code à maintenir pour un gain non mesuré.
// Refs: phase 95.
import { INTL_LOCALE } from '../lib/parisTime'
import type { Lang } from './types'

/**
 * Suffixe une clé de base par la forme plurielle qui convient au compte et à
 * la langue : `pluralKey('dashboard.sessions.available', 0, 'fr')` rend
 * `'dashboard.sessions.available_one'`.
 *
 * Le type de retour est littéral, donc `t(pluralKey(…))` ne compile que si les
 * DEUX clés `_one` / `_other` existent au catalogue — la faute de frappe est
 * attrapée par `make typecheck-webapp`, pas à l'exécution.
 */
export function pluralKey<K extends string>(
  base: K,
  n: number,
  lang: Lang,
): `${K}_one` | `${K}_other` {
  const category = new Intl.PluralRules(INTL_LOCALE[lang]).select(n)
  return category === 'one' ? `${base}_one` : `${base}_other`
}
